// DocumentsSection — der Dokumente-Reiter eines Kontakts als Windows-Explorer.
//
// Diese Komponente ist die DATENQUELLE (Adapter) für den FileExplorer und
// zugleich seine Shell: Suche, Modus-Umschalter, Upload und der
// Umbenennen-Dialog liegen hier, die Darstellung in
// documents/explorer/FileExplorer. Die Datei-Vorschau (PDF, Text, Bild) läuft
// über das gemeinsame Viewer-Fenster (documents/useDocumentViewer).
//
// BEWUSST NICHT weiter aufgeteilt: die Ref-Guards (listLoadedOnce,
// openedDeepLink) beobachten die `rows`-Liste. Lägen sie in
// einem anderen Bauteil als die Liste, risse die Deep-Link-Kette, ohne dass
// irgendetwas rot würde — der ⌘K-Treffer landete dann nur auf dem Tab.
//
// Die Props-Signatur ist unverändert; alle vier Aufrufstellen
// (ContactDetailsRedesign, Legacy-ContactDetails, Schadenmappe,
// AlteKundenaktenDetail) bleiben unangetastet.

import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogSurface,
  DialogTitle,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  Apps16Regular,
  ArrowUpload16Regular,
  Folder16Regular,
  Search16Regular,
} from "@fluentui/react-icons";
import { documentService, DocumentDto, DocumentFolder } from "../services/documentService";
import { useAuthOptional } from "../context/AuthContext";
import { dokumentListenCache } from "./documents/dokumentListenCache";
import { useOpenDocument } from "./documents/useDocumentViewer";
import { OfficeOpenDialog } from "./documents/OfficeOpenDialog";
import { fortschrittText, ladeInAkteHoch } from "./documents/akteUpload";
import { FileExplorer, BULK_CONFIRM_THRESHOLD, type ExplorerAuswahl } from "./documents/explorer/FileExplorer";
import { dokumentZuegeNutzlast, ZIEHTYP_DOKUMENT } from "./vorgang/detail/todoDrop";
import { fremdAblageArt, fremdAblageWas, leseAblageZug } from "./mail/postfachAblage";
import { ausNeuemOutlook, legeNeuesOutlookAb } from "./mail/neuesOutlook";
import { legeZugAbMitAnhangFrage, nachMailDateiUploadMitFrage } from "./documents/ablage/mailAnhangAblage";
import { useAblagePlatzhalter } from "./documents/ablage/useAblagePlatzhalter";
import { AblagePlatzhalterZeilen } from "./documents/ablage/AblagePlatzhalterAnzeige";
import type { AbgelegteDatei } from "./documents/explorer/dateiDrop";
import {
  freierName,
  liegtIn,
  mitAngelegtenOrdnern,
  normalizeFolderPath,
  parentPath,
  type ExplorerFile,
  type ExplorerFolder,
} from "./documents/explorer/explorerModel";
import { ordnerZaehlerLabel, vorgangsTag, vorgangsTagTitel } from "./documents/explorer/vorgangsdokumente";
import { baueKmvsStruktur, istKmvsPfad } from "./documents/explorer/kmvsOrdner";
import {
  VERBUND_WURZEL,
  fremdeAkte,
  istVerbundPfad,
  verbundeneKontakte,
  verbundOrdnerName,
  verbundPfad,
  type VerbundKontakt,
} from "./documents/explorer/verbundAkten";
import { fetchHaushalt, fetchUnternehmensverbund } from "./redesign/table/useUnternehmensverbund";
import { kundenakteOrdnerService } from "../services/kundenakteOrdnerService";
import { OrdnerWaehlenDialog } from "./documents/explorer/OrdnerWaehlenDialog";
import { AkteZuweisenPopup } from "./alteKundenakten/AkteZuweisenPopup";
import type { Folder as AkteOrdner } from "./alteKundenakten/types";
import { apiUrl } from "../utils/api";
import type { RdMenuItem } from "./redesign/table";
import { ConfirmDialog } from "./redesign/ConfirmDialog";
import { I } from "./redesign/kvpIcons";
import { KATEGORIE_MAIL_ARCHIV } from "./vorgang/detail/terminNotizen";
import { Toast } from "./redesign/Toast";
import { baseNameParts, dateinameOhneSchraegstrich, hatSchraegstrich, splitPath } from "../utils/fileUtils";
import { NamenEingabeDialog, SchraegstrichHinweis } from "./redesign/NamenEingabeDialog";
import { baueZip, eindeutigeNamen, type ZipEintrag } from "../utils/zipOhneKompression";
import { DOKUMENT_KATEGORIEN } from "../data/dokumentKategorien";
import { bestaetigen } from "./redesign/bestaetigen";

/* ---------------- Constants ---------------- */

const PAGE_SIZE = 50;
/** Kommt man nach so langer Zeit zurück in den Tab, wird still nachgesehen. */
const AUFFRISCHEN_NACH_MS = 60_000;
// Marking written by the legacy upload (see imports/.../upload-kundenakten.cjs):
// category='Alte Kundenakte'. The toggle filters server-side on exactly this.
const KUNDENAKTE_CATEGORY = "Alte Kundenakte";
/** Beschriftung der Wurzel im Ordnerbaum — auch in den Upload-Hinweisen. */
const WURZEL_LABEL = "Kundenakte";

/**
 * Die dauerhaft abgelegten Mails aus KMVS gehören nicht in diesen Reiter
 * (PO 15.09.2026: „Mails aus dem KMVS nicht anzeigen, sonst aus den
 * Kundenakten anzeigen").
 *
 * Unterschieden wird nach HERKUNFT, nicht nach Dateiendung — das ist die
 * Grenze, die der PO gezogen hat. Auf PROD sind 6.996 der 129.575 Dokumente
 * `.eml`, aber sie kommen aus drei verschiedenen Ecken:
 *
 *   Alte Kundenakte  5.155   mit dem Altbestand importiert  -> bleiben
 *   Schaden          1.309   hängen an Schadensfällen       -> bleiben
 *   E-Mail-Archiv      516   die Mail-Ablage von KMVS       -> raus
 *
 * Die ersten beiden sind Teil des importierten Ordnerbaums; wer die Akte
 * durchblättert, erwartet sie dort. Die dritte Gruppe hat einen eigenen Ort
 * (`GET /api/mail/archive`) und stand hier doppelt.
 *
 * Die Vorgangsansicht macht das längst so (`excludeCategory=E-Mail-Archiv,
 * Notizbild`) — der Kontakt-Reiter war schlicht nicht nachgezogen worden.
 * Notizbilder fallen im Backend ohnehin per Default heraus.
 */
const AUSGEBLENDETE_KATEGORIEN = [KATEGORIE_MAIL_ARCHIV];
const TREE_WIDTH_KEY = "kvp.docexplorer.treewidth";
/**
 * Der Ordner „KMVS" an der Wurzel der Akte (PO 28.09.2026) ersetzt die frühere
 * Gruppe „Vorgänge": darunter Kontakt, Risiken, Verträge, Schäden, Vorgänge (je
 * Vorgang ein Ordner mit dem Betreff) und Zahlungsverkehr. Gebaut wird er aus
 * allen KMVS-Dokumenten des Kontakts — alles außer der Alten Kundenakte und der
 * Mail-Ablage (`documents/explorer/kmvsOrdner.ts`).
 *
 * Deckel statt Paging: Es sind je Kontakt Dutzende, selten Hunderte. Liegt der
 * Bestand darüber, sagt das Zähler-Label, dass „KMVS Dokumente" den Rest hat.
 */
const KMVS_DOKUMENTE_LIMIT = 500;

/** Leere Office-Dateien für „Neu → Word/Excel/PowerPoint" (liegen in `public/vorlagen`). */
const NEUE_DATEI = {
  docx: {
    name: "Neues Word-Dokument",
    mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  },
  xlsx: {
    name: "Neue Excel-Tabelle",
    mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  },
  pptx: {
    name: "Neue PowerPoint-Präsentation",
    mime: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  },
} as const;
type NeueDateiArt = keyof typeof NEUE_DATEI;

/** Ordner-Download als ZIP: Seitengröße der Abfrage und Obergrenzen (Speicher im Browser). */
const ZIP_SEITE = 500;
const ZIP_MAX_DATEIEN = 3000;
const ZIP_MAX_BYTES = 1024 * 1024 * 1024;

/** Unzulässig in Ordnernamen: Trenner und was Windows ohnehin verbietet. */
const UNGUELTIGE_ZEICHEN = /[\\/:*?"<>|]/;
/** Stabile Leer-Referenz: ein frisches `[]` je Render würde im FileExplorer
 *  Memos und Effects unnötig neu auslösen. */
const EMPTY_FOLDERS: ExplorerFolder[] = [];
const EMPTY_PFADE: string[] = [];

/* ---------------- Helpers ---------------- */

// Eine Regel, eine Quelle: die Typ-Erkennung liegt in `documents/documentKind`
// (Task-9-Review, Carry-Over B) statt lokal eigene Logik zu pflegen.

/** DocumentDto → Explorer-Zeile. Legacy-Kundenakten tragen den ordner-relativen
 *  Pfad im `fileName`; der Basename ist der Anzeigename, das Verzeichnis die
 *  graue Unterzeile (nur in der flachen Liste/Suche relevant). */
const toExplorerFile = (d: DocumentDto): ExplorerFile<DocumentDto> => {
  const { dir, base } = splitPath(d.fileName);
  return {
    kind: "file",
    key: String(d.id),
    name: base || d.fileName,
    dir,
    size: d.fileSize,
    date: d.uploadedAt,
    mimeType: d.mimeType,
    snippet: d.snippet ?? null,
    category: d.category ?? null,
    // P6: Herkunft sichtbar machen (Regeln in explorer/vorgangsdokumente.ts).
    tag: vorgangsTag(d.vorgang),
    tagTitle: vorgangsTagTitel(d.vorgang),
    // Papierkorb: gelöschte Zeilen erscheinen nur im Modus „Gelöschte
    // anzeigen" und dort gedimmt — sie sehen anders aus als das, was in der
    // Akte wirklich liegt.
    deleted: d.deleted ?? null,
    dimmed: !!d.deleted,
    payload: d,
  };
};

const toExplorerFolders = (list: DocumentFolder[], prefix: string): ExplorerFolder[] =>
  list
    // Namenlose Einträge verwerfen: der Import-Worker kürzt `fileName` auf die
    // LETZTEN 255 Zeichen und wirft damit bei sehr tiefen Pfaden den Präfix weg.
    // Die serverseitige Ordner-Aggregation kann daraus ein leeres erstes Segment
    // ableiten — das wäre eine namenlose, nicht navigierbare Zeile im Baum.
    .filter((f) => f.name.trim().length > 0)
    .map((f) => ({ kind: "folder" as const, name: f.name, path: prefix + f.name + "/", count: f.count }));

const readTreeWidth = (): number => {
  try {
    const raw = Number(localStorage.getItem(TREE_WIDTH_KEY));
    return Number.isFinite(raw) && raw >= 150 && raw <= 520 ? raw : 240;
  } catch {
    return 240;
  }
};

/* ---------------- Styles ---------------- */

const useStyles = makeStyles({
  empty: { padding: "32px", textAlign: "center", color: tokens.colorNeutralForeground3 },
  renameBody: { display: "flex", flexDirection: "column", gap: "8px", marginTop: "8px" },
  dirHint: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  uploadErr: { color: tokens.colorPaletteRedForeground1, fontSize: tokens.fontSizeBase200 },
  // Native <select> statt Fluent-Dropdown: die Toolbar ist durchgehend aus
  // schlichten Elementen mit `docx-*`-Klassen gebaut, ein Fluent-Dropdown
  // brächte eine zweite Optik in dieselbe Leiste. Die Maße sind von
  // `.docx-seg` / `.docx-search` übernommen (contactRedesign.css:1514-1519).
  //
  // Die Refined-Variablen bekommen Fallbacks: derselbe Stil trägt auch den
  // Select im Dialog, und der rendert in einem Fluent-Portal — außerhalb von
  // `.kvp-rd` wäre `var(--line-2)` sonst leer und der Rahmen verschwände.
  catSelect: {
    maxWidth: "230px",
    borderRadius: "9px",
    border: "1.5px solid var(--line-2, #ddd3dc)",
    backgroundColor: "var(--field, #ffffff)",
    color: "var(--ink, #241522)",
    fontFamily: 'var(--ui, "Hanken Grotesk", system-ui, sans-serif)',
    fontSize: "13px",
    fontWeight: 650,
    paddingLeft: "9px",
    paddingRight: "9px",
    paddingTop: "7px",
    paddingBottom: "7px",
    cursor: "pointer",
  },
});

/* ---------------- Component ---------------- */

interface Props {
  contactId: number;
  /** Deep-Link: Dokument-ID, die beim Laden der Liste automatisch geöffnet wird (⌘K-Treffer, Task 10). */
  openDocId?: number;
  /** Suchbegriff für den Fundstellen-Sprung im PDF-Viewer (⌘K-Treffer, Task 10). */
  searchTerm?: string;
  /**
   * Nur die Alte Kundenakte, ohne den Ordner „KMVS" und ohne „Alte
   * Kundenakte zuweisen" — die Detailseite eines Kundenakten-Ordners zeigt
   * genau diesen einen Ordner.
   */
  nurAlteAkte?: boolean;
  /**
   * Deep-Link: dieser Ordner der Kundenakte (Explorer-Pfad, mit „/" am Ende)
   * wird beim Öffnen gezeigt — z. B. der Schaden-Ordner, in den eben eine Mail
   * abgelegt wurde (`/contacts/<id>?tab=Dokumente&ordner=…`).
   */
  openPfad?: string | null;
}

export const DocumentsSection: React.FC<Props> = ({ contactId, openDocId, searchTerm, nurAlteAkte = false, openPfad = null }) => {
  const styles = useStyles();
  const validContact = Number.isFinite(contactId) && contactId > 0;
  // Eine Entscheidung fuer alle Aufrufer: PDF/Text/Bild/Mail in den Viewer,
  // Word/Excel/PowerPoint in den Office-Editor, der Rest in den Download.
  const oeffneDokument = useOpenDocument();
  // Pro Instanz eindeutig: die ID war früher global ("docsScrollable"). Zwei
  // gleichzeitig gemountete Sektionen ließen InfiniteScroll sonst am falschen
  // Element hängen.
  const uid = React.useId().replace(/:/g, "");
  const scrollId = `docsScrollable-${uid}`;

  // `navigate` ist hier schon die Ordner-Navigation des Explorers — der
  // Router-Hook bekommt deshalb einen eigenen Namen.
  const routerNavigate = useNavigate();
  const [rows, setRows] = useState<DocumentDto[]>([]);
  // Alle KMVS-Dokumente des Kontakts — daraus entsteht der Ordner „KMVS"
  // (s. KMVS_DOKUMENTE_LIMIT). Eigene Quelle neben der gepagten Akten-Liste.
  const [kmvsRows, setKmvsRows] = useState<DocumentDto[]>([]);
  /**
   * Sofort sichtbare Ablage (User 01.11.2026): Platzhalter stehen ab dem Drop
   * in der Liste und werden geräumt, sobald die echte Liste neu geladen ist.
   */
  const ablage = useAblagePlatzhalter();
  const ablageRaeumen = ablage.raeumen;
  useEffect(() => {
    ablageRaeumen();
  }, [rows, kmvsRows, ablageRaeumen]);
  const [kmvsTotal, setKmvsTotal] = useState(0);
  // Selbst angelegte (auch leere) Ordner der Akte — vollständige Pfade.
  const [angelegt, setAngelegt] = useState<string[]>([]);
  // Alte Kundenakten, die diesem Kontakt schon zugewiesen sind — für die leere
  // Akte: läuft der Import noch, steht das dort statt des Zuweisen-Knopfs.
  const [zuweisungen, setZuweisungen] = useState<AkteOrdner[] | null>(null);
  const [strukturKey, setStrukturKey] = useState(0);
  // Alte Kundenakten von Kontakten im selben Haushalt / Unternehmensverbund
  // (PO 28.09.2026) — nur die, in denen wirklich etwas liegt.
  const [verbundAkten, setVerbundAkten] = useState<Array<VerbundKontakt & { anzahl: number }>>([]);
  const [nameDialog, setNameDialog] = useState<
    | { art: "neu"; eltern: string; name: string }
    | { art: "umbenennen"; alt: ExplorerFolder; name: string }
    | null
  >(null);
  const [nameErr, setNameErr] = useState<string | null>(null);
  const [nameBusy, setNameBusy] = useState(false);
  const [ordnerLoeschen, setOrdnerLoeschen] = useState<ExplorerFolder | null>(null);
  const [zuweisenOffen, setZuweisenOffen] = useState(false);
  const [search, setSearch] = useState("");
  // Explorer = Windows-Explorer-Ansicht der Alten Kundenakten (Ordner + Dateien);
  // Flat = flache Liste ALLER Dokumente (bisheriges Verhalten).
  const [mode, setMode] = useState<"explorer" | "flat">("explorer");
  // Papierkorb (PO 16.09.2026): aus = nur aktive Dokumente (Normalfall), an =
  // NUR die gelöschten. Bewusst ein Entweder-oder statt einer Mischliste: in
  // einer gemischten Liste ist nicht zu erkennen, was noch in der Akte liegt,
  // und der Zähler daneben wäre nicht mehr zu deuten.
  const [zeigePapierkorb, setZeigePapierkorb] = useState(false);
  // Aktueller Ordnerpfad im Explorer-Modus: "" = Wurzel, sonst auf "/" endend.
  const [currentPath, setCurrentPath] = useState("");
  // Ordner-Cache je Ebene für den Baum (Schlüssel = Pfad, "" = Wurzel).
  const [folderCache, setFolderCache] = useState<Record<string, ExplorerFolder[]>>({});
  const [expandedPaths, setExpandedPaths] = useState<ReadonlySet<string>>(() => new Set<string>());
  const [treeWidth, setTreeWidth] = useState(readTreeWidth);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  // Kategorie des NÄCHSTEN Uploads. "" = ohne Kategorie — das bisherige und
  // weiterhin richtige Verhalten für alles, was in keine der drei fachlichen
  // Schubladen gehört. Bewusst kein Pflichtfeld: `Document.category` ist
  // varchar(100)-Freitext, 128.456 der 128.473 PROD-Dokumente haben ihren Wert
  // maschinell bekommen, und ein Zwang zur Auswahl erzeugte nur Rateeinträge.
  const [uploadCategory, setUploadCategory] = useState("");
  // Mehrere Dateien/ganze Ordner brauchen eine sichtbare Laufzeit: der Upload
  // geht der Reihe nach, und bei einem Rate-Limit steht er kurz still. Ohne
  // Anzeige sähe beides gleich aus wie ein Hänger.
  const [uploadFortschritt, setUploadFortschritt] = useState<{ erledigt: number; gesamt: number } | null>(null);
  const [uploadWartet, setUploadWartet] = useState<number | null>(null);
  const uploadAbbruch = useRef(false);
  const [rename, setRename] = useState<{ doc: DocumentDto; name: string; ext: string } | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [renameErr, setRenameErr] = useState<string | null>(null);
  const [deleteErr, setDeleteErr] = useState<string | null>(null);
  // Nachträgliches Zuordnen. Ohne diesen Weg wäre eine einmal falsch gewählte
  // Kategorie nur durch Löschen und erneutes Hochladen zu korrigieren — und die
  // 27 Satzungen, die schon als „Alte Kundenakte" im Bestand liegen, ließen sich
  // gar nicht einordnen.
  const [catEdit, setCatEdit] = useState<{ doc: DocumentDto; value: string } | null>(null);
  const [catSaving, setCatSaving] = useState(false);
  const [catErr, setCatErr] = useState<string | null>(null);
  const [bulkAsk, setBulkAsk] = useState<ExplorerFile<DocumentDto>[] | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const ordnerRef = useRef<HTMLInputElement>(null);
  // Deep-Link-Fallback (Task 10, Carry-Over A): wird im Finally des initialen
  // Listen-Loads gesetzt, damit der Auto-Open-Effect nicht schon auf der noch
  // leeren Erst-Render-Row-Liste feuert.
  const listLoadedOnce = useRef(false);
  // Bricht den Vorgänger-Request ab, wenn schnell durch Ordner geklickt wird —
  // sonst könnte eine langsame ältere Antwort eine neuere überschreiben.
  const inflight = useRef<AbortController | null>(null);
  // Zählt bei jedem Ordner-/Modus-/Suchwechsel hoch. `fetchMore` merkt sich den
  // Stand beim Start und verwirft seine Antwort, wenn sie inzwischen veraltet
  // ist (Nachlade-Requests laufen ohne AbortSignal weiter).
  const listGeneration = useRef(0);
  // Neu laden, ohne die Liste zu räumen: nach Hochladen, Umbenennen, Löschen …
  // bleibt der Ordner derselbe, und ein leerer Moment sähe aus, als wären die
  // Dateien weg (User 01.11.2026: „dann geht alles kurz weg und es lädt").
  const stillNeuLaden = useRef(false);
  const angezeigteListe = useRef("");
  const letztesLaden = useRef(0);
  const userId = String(useAuthOptional()?.user?.id ?? "");

  // Explorer OHNE Suchbegriff = Ordner-Browsing (Breadcrumb + Ordnerzeilen).
  const explorerBrowse = mode === "explorer" && !search.trim();

  /**
   * Wessen Akte an einem Pfad liegt: die eigene, oder — im Teilbaum der
   * verbundenen Akten — die des Haushalts-/Verbundmitglieds mit dem Ordner darin.
   */
  const quelle = (pfad: string): { contactId: number; prefix: string } => {
    const f = fremdeAkte(pfad);
    return f ? { contactId: f.id, prefix: f.innen } : { contactId, prefix: pfad };
  };

  const buildFilter = (off: number) => ({
    contactId: explorerBrowse ? quelle(currentPath).contactId : contactId,
    limit: PAGE_SIZE,
    offset: off,
    // „KMVS Dokumente" heißt: alles, was IN KMVS zu diesem Kontakt entstanden
    // ist — und nur das. Der Knopf zeigte bis dahin auch den kompletten
    // Altbestand, womit die beiden Knöpfe dasselbe versprachen und der rechte
    // den linken mit einschloss (PO 20.09.2026: „bei KMVS-Dokumente dann nur
    // die KMVS zugewiesenen Dateien anzeigen"). Auf PROD sind das 5-stellige
    // Zahlen je Kontakt — der Altbestand hat die paar KMVS-Dokumente schlicht
    // begraben.
    excludeCategory:
      mode === "flat" ? [...AUSGEBLENDETE_KATEGORIEN, KUNDENAKTE_CATEGORY] : AUSGEBLENDETE_KATEGORIEN,
    ...(search.trim() ? { search: search.trim() } : {}),
    // Explorer-Modus ist immer auf die Alten Kundenakten begrenzt.
    ...(mode === "explorer" ? { category: KUNDENAKTE_CATEGORY } : {}),
    // Nur beim Ordner-Browsing (ohne Suche) auf die Ebene `currentPath` begrenzen.
    ...(explorerBrowse ? { pathPrefix: quelle(currentPath).prefix } : {}),
    // Im Ordner-Browsing nach Namen sortieren — ein nach Upload-Datum
    // sortierter Ordnerinhalt wirkt willkürlich. Sonst bleibt der
    // Server-Default (uploadedAt absteigend) unverändert.
    ...(explorerBrowse ? { sort: "name" as const, dir: "asc" as const } : {}),
    // Papierkorb: ohne den Schalter geht der Parameter gar nicht mit — der
    // Server-Default ist „nur aktive".
    ...(zeigePapierkorb ? { papierkorb: "geloescht" as const } : {}),
  });

  // Neuer Kontakt: Explorer-Wurzel und Baum-Cache zurücksetzen. Die Akte
  // öffnet IMMER in „Alte Kundenakte" (PO 28.09.2026: „aktuell springt es zu
  // KMVS-Dokumente") — auch wenn sie leer ist; dann steht dort der Weg, eine
  // alte Kundenakte zuzuweisen, und der Ordner „KMVS" mit allem aus KMVS.
  useEffect(() => {
    setMode("explorer");
    setZeigePapierkorb(false);
    setCurrentPath("");
    setFolderCache({});
    setExpandedPaths(new Set());
  }, [contactId]);

  // Deep-Link auf einen Ordner: NACH dem Zurücksetzen oben (gleicher Commit,
  // der letzte Setter gewinnt) den Ordner öffnen, den Baum bis dorthin
  // aufklappen und die Ebenen der Vorfahren nachladen — die eigene Ebene holt
  // der Lade-Effect ohnehin, sobald `currentPath` wechselt.
  useEffect(() => {
    if (!openPfad || !validContact) return;
    const p = normalizeFolderPath(openPfad);
    if (!p) return;
    setMode("explorer");
    setCurrentPath(p);
    const vorfahren: string[] = [];
    let acc = "";
    for (const s of p.split("/").filter(Boolean)) {
      acc += s + "/";
      vorfahren.push(acc);
    }
    setExpandedPaths(new Set(vorfahren));
    for (const v of ["", ...vorfahren.slice(0, -1)]) void loadFolderLevel(v);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, openPfad]);

  const switchMode = (m: "explorer" | "flat") => {
    setCurrentPath("");
    setMode(m);
  };

  /** Lädt die Unterordner einer Ebene in den Cache (für den Baum). */
  const loadFolderLevel = React.useCallback(
    async (path: string) => {
      if (!validContact || istKmvsPfad(path)) return;
      try {
        const list = await documentService.listFolders({
          ...quelle(path),
          category: KUNDENAKTE_CATEGORY,
        });
        setFolderCache((prev) => ({ ...prev, [path]: toExplorerFolders(list, path) }));
      } catch {
        // Ebene bleibt ungeladen; das Aufklappen lässt sich wiederholen.
      }
    },
    [contactId, validContact],
  );

  const onToggleExpand = (path: string) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
        if (!folderCache[path]) void loadFolderLevel(path);
      }
      return next;
    });
  };

  /** Ordner öffnen: Liste umschalten und den Pfad im Baum mit aufklappen,
   *  damit der Baum die geöffnete Stelle zeigt. */
  const navigate = (path: string) => {
    // Die Sammelwurzel der verbundenen Akten ist kein Ordner — „eine Ebene
    // höher" aus einer fremden Akte führt zurück an die eigene Wurzel.
    const p = normalizeFolderPath(path) === VERBUND_WURZEL ? "" : normalizeFolderPath(path);
    setCurrentPath(p);
    // KEIN eigener Ordner-Fetch hier: der Lade-Effect holt die Ebene ohnehin,
    // sobald `currentPath` wechselt. Ein zusätzlicher Aufruf verdoppelte nur
    // jeden GET /documents/folders beim Erstbesuch eines Ordners.
    if (p) {
      setExpandedPaths((prev) => {
        const next = new Set(prev);
        // Alle Vorfahren des Ziels aufklappen.
        let acc = "";
        for (const s of p.split("/").filter(Boolean)) {
          acc += s + "/";
          next.add(acc);
        }
        return next;
      });
    }
  };

  // Initial load + reload on mode/path/search change (debounced). Im Explorer-
  // Browsing werden zusätzlich die Unterordner der aktuellen Ebene geladen.
  useEffect(() => {
    if (!validContact) {
      setRows([]);
      setHasMore(false);
      setTotal(0);
      return;
    }
    let active = true;
    // Neue Generation: laufende Nachlade-Requests werden dadurch entwertet.
    listGeneration.current += 1;
    // Still nur, wenn es dieselbe Liste bleibt: Wechselt gleichzeitig der Ordner,
    // dürfen dessen Vorgänger nicht stehen bleiben.
    const liste = JSON.stringify([contactId, search, mode, currentPath, zeigePapierkorb]);
    const still = stillNeuLaden.current && angezeigteListe.current === liste;
    stillNeuLaden.current = false;
    angezeigteListe.current = liste;
    const trimmed = search.trim();
    const browse = mode === "explorer" && !trimmed;
    const imKmvsOrdner = browse && istKmvsPfad(currentPath);
    // Suchtreffer ändern sich mit jedem Buchstaben, sie bleiben außen vor.
    const schluessel =
      !trimmed && !imKmvsOrdner
        ? { userId, contactId, ansicht: [mode, currentPath, zeigePapierkorb] }
        : null;
    const ausCache = !still && schluessel ? dokumentListenCache.lesen<DocumentDto>(schluessel) : null;
    // So viele Zeilen wie gerade geladen, sonst spränge die Liste nach dem
    // Speichern aus dem Weitergescrollten zurück auf die erste Seite.
    const geladen = Math.max(still ? rows.length : ausCache ? ausCache.rows.length : 0, PAGE_SIZE);
    if (ausCache) {
      // Sofort anzeigen; die Anfrage unten ersetzt den Stand gleich durch den
      // des Servers.
      setRows(ausCache.rows);
      setOffset(ausCache.rows.length);
      setHasMore(ausCache.hasMore);
      setTotal(ausCache.total);
      setLoading(false);
    } else if (!still) {
      setLoading(true);
      // Zeilen der vorherigen Ebene sofort räumen. Sonst blieben sie während des
      // Debounce sichtbar UND anklickbar — im Browsing ohne Pfadzeile sähe es
      // aus, als lägen sie im neu geöffneten Ordner.
      setRows([]);
      setOffset(0);
      setHasMore(false);
    }
    // Im Ordner „KMVS" gibt es nichts zu holen: seine Dateien liegen schon in
    // `kmvsRows`, geladen vom eigenen Effect unten.
    if (imKmvsOrdner) {
      setTotal(0);
      setLoading(false);
      listLoadedOnce.current = true;
      return () => {
        active = false;
      };
    }
    const t = setTimeout(async () => {
      inflight.current?.abort();
      const ctrl = new AbortController();
      inflight.current = ctrl;
      try {
        const foldersP = browse
          ? documentService.listFolders(
              {
                ...quelle(currentPath),
                category: KUNDENAKTE_CATEGORY,
                // Papierkorb: derselbe Modus wie die Dateiliste, sonst zählte
                // ein Ordner Dateien mit, die daneben nicht stehen.
                ...(zeigePapierkorb ? { papierkorb: "geloescht" as const } : {}),
              },
              ctrl.signal,
            )
          : Promise.resolve([] as DocumentFolder[]);
        const [folderList, page] = await Promise.all([
          foldersP,
          documentService.listPaged({ ...buildFilter(0), limit: geladen }, ctrl.signal),
        ]);
        if (!active || ctrl.signal.aborted) return;
        if (browse) {
          setFolderCache((prev) => ({
            ...prev,
            [currentPath]: toExplorerFolders(folderList, currentPath),
          }));
        }
        // Ein währenddessen laufendes Nachladen rechnet noch mit der alten Liste.
        listGeneration.current += 1;
        const mehr = page.items.length === geladen;
        setRows(page.items);
        setOffset(page.items.length);
        setHasMore(mehr);
        setTotal(page.total);
        letztesLaden.current = Date.now();
        if (schluessel) {
          dokumentListenCache.schreiben(schluessel, { rows: page.items, total: page.total, hasMore: mehr });
        }
      } catch (e) {
        // Ein abgebrochener Request ist KEIN Fehler — sonst würde ein schneller
        // Ordnerwechsel die gerade geladene Liste leeren.
        const aborted = e instanceof DOMException && e.name === "AbortError";
        // Beim stillen Neuladen bleibt der bisherige Stand stehen: Die Änderung
        // selbst ist gespeichert, nur die Bestätigung kam nicht an. Anders, wenn
        // der Server den Zugriff verweigert: Dann darf nichts stehen bleiben.
        // Ein gemerkter Stand wird nach jedem Fehler verworfen — er wäre sonst
        // nicht mehr vom aktuellen zu unterscheiden.
        const keinZugriff = /: 40[134]$/.test(e instanceof Error ? e.message : "");
        if (active && !aborted) {
          if (schluessel) dokumentListenCache.verwerfen(schluessel);
          if (!still || keinZugriff) {
            setRows([]);
            setHasMore(false);
            setTotal(0);
          }
        }
      } finally {
        if (active && !ctrl.signal.aborted) {
          setLoading(false);
          listLoadedOnce.current = true;
        }
      }
    }, still || ausCache ? 0 : 250);
    return () => {
      active = false;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, search, mode, currentPath, reloadKey, zeigePapierkorb]);

  // Der Ordner „KMVS", die selbst angelegten Ordner und der Stand der
  // Zuweisung — nicht bei jedem Ordnerwechsel.
  useEffect(() => {
    if (!validContact || mode !== "explorer") return;
    const ctrl = new AbortController();
    if (!nurAlteAkte) {
      documentService
        .listPaged(
          {
            contactId,
            excludeCategory: [...AUSGEBLENDETE_KATEGORIEN, KUNDENAKTE_CATEGORY],
            limit: KMVS_DOKUMENTE_LIMIT,
            offset: 0,
            sort: "name",
            dir: "asc",
            ...(zeigePapierkorb ? { papierkorb: "geloescht" as const } : {}),
          },
          ctrl.signal,
        )
        .then((page) => {
          setKmvsRows(page.items);
          setKmvsTotal(page.total);
        })
        .catch(() => undefined);
    }
    return () => ctrl.abort();
  }, [contactId, validContact, mode, reloadKey, strukturKey, zeigePapierkorb, nurAlteAkte]);

  // Verbundene Akten, Zuweisung und angelegte Ordner ändern sich nicht durch
  // eine einzelne Datei — sie laufen deshalb nicht bei jedem Neuladen mit.
  useEffect(() => {
    if (!validContact || mode !== "explorer") return;
    const ctrl = new AbortController();
    if (!nurAlteAkte) {
      // Haushalt und Unternehmensverbund: beide fragen — der Server antwortet
      // für eine Person beim Verbund bzw. für eine Firma beim Haushalt leer.
      // Mitarbeiter/Ansprechpartner sehen die Akte ihrer Firma NICHT
      // (08.10.2026: „nur im Firmenverbund, nicht bei den Mitarbeitern").
      Promise.all([
        fetchHaushalt(contactId, ctrl.signal).catch(() => null),
        fetchUnternehmensverbund(contactId, ctrl.signal).catch(() => null),
      ])
        .then(async ([haushalt, verbund]) => {
          const kandidaten = verbundeneKontakte(contactId, haushalt, verbund).slice(0, 12);
          // Nur zeigen, wo eine Akte liegt. Ein 403 (keine Sicht auf den
          // Inhaber) lässt den Ordner einfach weg.
          const mitAkte = await Promise.all(
            kandidaten.map((k) =>
              documentService
                .listPaged({ contactId: k.id, category: KUNDENAKTE_CATEGORY, limit: 1, offset: 0 }, ctrl.signal)
                .then((pg) => ({ ...k, anzahl: pg.total }))
                .catch(() => ({ ...k, anzahl: 0 })),
            ),
          );
          if (!ctrl.signal.aborted) setVerbundAkten(mitAkte.filter((k) => k.anzahl > 0));
        })
        .catch(() => undefined);
      fetch(apiUrl("kundenakten-folders", { contactId: String(contactId), limit: 20 }), { signal: ctrl.signal })
        .then((r) => (r.ok ? r.json() : []))
        .then((d) => setZuweisungen(Array.isArray(d) ? d : []))
        .catch(() => undefined);
    }
    kundenakteOrdnerService
      .liste(contactId, ctrl.signal)
      .then(setAngelegt)
      .catch(() => undefined);
    return () => ctrl.abort();
  }, [contactId, validContact, mode, strukturKey, zeigePapierkorb, nurAlteAkte]);

  const fetchMore = async () => {
    if (loading || !hasMore) return;
    // Generationsschutz: wechselt der Nutzer während des Nachladens den Ordner,
    // den Modus oder den Suchbegriff, darf die eintreffende Seite NICHT an die
    // inzwischen ausgetauschte Liste angehängt werden — sonst stünden Dateien
    // des alten Ordners im neuen (im Browsing ohne graue Pfadzeile nicht einmal
    // erkennbar) und `offset` wäre zu hoch, sodass beim Weiterscrollen eine
    // Seite übersprungen würde.
    const gen = listGeneration.current;
    try {
      const { items } = await documentService.listPaged(buildFilter(offset));
      if (gen !== listGeneration.current) return;
      setRows((prev) => [...prev, ...items]);
      setOffset((prev) => prev + items.length);
      if (items.length < PAGE_SIZE) setHasMore(false);
    } catch {
      if (gen === listGeneration.current) setHasMore(false);
    }
  };

  const reload = () => {
    stillNeuLaden.current = true;
    dokumentListenCache.kontaktVerwerfen(contactId);
    setReloadKey((k) => k + 1);
  };

  /**
   * Eigene Änderungen stehen sofort in der Liste, und zwar aus der Antwort des
   * Servers. Danach läuft immer `reload()`: Es bricht ältere Anfragen ab und
   * gleicht ab, was hier niemand wissen kann (Ordnerzahlen, Änderungen anderer).
   */
  const zeileErsetzen = (doc: DocumentDto) => {
    const ersetzen = (liste: DocumentDto[]) => liste.map((d) => (d.id === doc.id ? { ...d, ...doc } : d));
    setRows(ersetzen);
    setKmvsRows(ersetzen);
  };
  const zeileEntfernen = (id: number) => {
    if (rows.some((d) => d.id === id)) {
      setTotal((t) => Math.max(0, t - 1));
      setOffset((o) => Math.max(0, o - 1));
    }
    setRows((prev) => prev.filter((d) => d.id !== id));
    setKmvsRows((prev) => prev.filter((d) => d.id !== id));
  };

  // Änderungen anderer erreichen diese Ansicht nicht von selbst: Beim Zurückkommen
  // in den Tab wird nachgesehen, höchstens einmal je Minute.
  useEffect(() => {
    const zurueck = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - letztesLaden.current < AUFFRISCHEN_NACH_MS) return;
      reload();
    };
    document.addEventListener("visibilitychange", zurueck);
    return () => document.removeEventListener("visibilitychange", zurueck);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  const triggerBlobDownload = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const download = async (doc: DocumentDto) => {
    try {
      const blob = await documentService.fetchBlob(doc.id);
      triggerBlobDownload(blob, splitPath(doc.fileName).base || `dokument-${doc.id}`);
    } catch (e) {
      console.error("download failed", e);
      setToast("Download fehlgeschlagen.");
    }
  };

  /** Sammel-Download: sequenziell, damit der Browser nicht blockt. */
  const downloadMany = async (list: ExplorerFile<DocumentDto>[]) => {
    let failed = 0;
    for (const f of list) {
      if (!f.payload) continue;
      try {
        const blob = await documentService.fetchBlob(f.payload.id);
        triggerBlobDownload(blob, f.name);
      } catch {
        failed++;
      }
    }
    setToast(
      failed === 0
        ? `${list.length} Dateien heruntergeladen.`
        : `${list.length - failed} von ${list.length} heruntergeladen, ${failed} fehlgeschlagen.`,
    );
  };

  const requestBulkDownload = (list: ExplorerFile<DocumentDto>[]) => {
    if (list.length >= BULK_CONFIRM_THRESHOLD) setBulkAsk(list);
    else void downloadMany(list);
  };

  /**
   * Klick auf eine Datei. Die Entscheidung „Viewer, Editor oder Download?"
   * trifft `useOpenDocument` — dieselbe Stelle, die auch Vorgang, To-do und
   * Kundentagebuch fragen.
   *
   * Bis dahin hatte dieser Reiter eine eigene, kürzere Antwort: alles, was der
   * Browser nicht selbst anzeigen kann, ging in den Download. Word- und
   * Excel-Dateien fielen damit genau hier durch — im Vorgang öffneten sie
   * längst den Office-Editor, in der Kundenakte landeten sie im
   * Download-Ordner (PO 20.09.2026).
   *
   * `download` bekommt die Dokument-Id und ignoriert sie: das DTO liegt hier
   * schon vor, und nur daraus kommt der Anzeigename der heruntergeladenen Datei.
   */
  const openPreview = (doc: DocumentDto) => {
    oeffneDokument(doc, { download: () => void download(doc) });
  };

  // Deep-Link (⌘K-Treffer, Task 10): sobald die Liste das Ziel-Dokument kennt,
  // automatisch öffnen — PDF/Text fullpage mit Suchbegriff, Bilder über den
  // bestehenden Dialog-Pfad. Liegt das Dokument nicht auf der geladenen
  // Listen-Seite (PAGE_SIZE) oder wird es vom lokalen Filter/Suchbegriff
  // ausgeblendet, würde der Effect sonst still verpuffen — Fallback: einzeln
  // per GET /documents/:id nachladen und direkt öffnen (Review-Carry-Over A).
  // Deep-Link-Auto-Open: pro doc-id+Suchbegriff genau einmal öffnen — ein NEUER
  // ?doc=-Param ODER derselbe ?doc= mit anderem ?dq= (zweiter ⌘K-Treffer auf
  // dieselbe Seite mit anderem Suchbegriff) öffnet wieder.
  const openedDeepLink = React.useRef<string | null>(null);
  React.useEffect(() => {
    const deepLinkKey = openDocId ? `${openDocId}|${searchTerm ?? ""}` : null;
    if (!deepLinkKey || openedDeepLink.current === deepLinkKey || !listLoadedOnce.current) return;
    // searchTerm nur beim Viewer-Weg — er trägt das Treffer-Highlight im PDF.
    const openDeepLinkDoc = (d: DocumentDto) => {
      oeffneDokument(d, { download: () => void download(d), searchTerm });
    };
    const doc = rows.find((d) => d.id === openDocId);
    // Weiterhin SYNCHRON vor dem Öffnen/fetchOne setzen — Race-Absicherung gegen
    // doppeltes Öffnen durch Effect-Re-Runs (rows-Änderung während fetchOne läuft).
    openedDeepLink.current = deepLinkKey;
    if (doc) {
      openDeepLinkDoc(doc);
      return;
    }
    documentService
      .fetchOne(openDocId as number)
      .then(openDeepLinkDoc)
      .catch((e) => console.warn("Deep-Link: Dokument konnte nicht per Einzel-Fetch geladen werden", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDocId, rows, searchTerm]);

  // Umbenennen: Basename OHNE Verzeichnisteil und OHNE Extension vorbefüllen; die
  // Extension bleibt fixer, nicht editierbarer Suffix (steuert Download-/Preview-Typ).
  const openRename = (doc: DocumentDto) => {
    const { name, ext } = baseNameParts(doc.fileName);
    setRenameErr(null);
    setRename({ doc, name, ext });
  };

  const closeRename = () => {
    setRename(null);
    setRenameErr(null);
  };

  const saveRename = async () => {
    if (!rename) return;
    // T-456: „/“ trennt Ordner — er wird zu „-“, wie das Popup vorab zeigt.
    const next = dateinameOhneSchraegstrich(rename.name.trim());
    if (!next) {
      setRenameErr("Bitte einen Namen eingeben.");
      return;
    }
    setRenaming(true);
    setRenameErr(null);
    try {
      // Nur der neue Basename wird geschickt; das Backend erhält Verzeichnisteil
      // und Extension des Bestands und rührt die Storage-Datei nicht an.
      const neu = await documentService.update(rename.doc.id, { fileName: next });
      zeileErsetzen(neu);
      setRename(null);
      reload();
    } catch (e) {
      setRenameErr(e instanceof Error ? e.message : "Umbenennen fehlgeschlagen");
      // Vielleicht hat jemand anderes die Datei gerade geändert oder gelöscht.
      reload();
    } finally {
      setRenaming(false);
    }
  };

  /**
   * Löschen = Papierkorb (PO 16.09.2026). `DELETE /api/documents/:id` setzt
   * seither nur noch eine Marke; die Datei bleibt im Speicher liegen und kommt
   * über „Gelöschte anzeigen" → „Wiederherstellen" zurück. Endgültig löschen
   * kann nur ein Administrator, und nur über die API — von hier aus gar nicht.
   *
   * Die Rückfrage bleibt trotzdem, und sie nennt die Datei beim Namen: ein
   * Fehlgriff kostet zwar keine Daten mehr, aber immer noch die Zeit, den
   * Papierkorb zu suchen.
   *
   * Sie steht hier und nicht im FileExplorer: nur diese Ebene kennt den
   * Kontext und lädt danach neu.
   */
  const loeschen = async (doc: DocumentDto) => {
    const ok = await bestaetigen({
      titel: "Datei löschen?",
      text: `„${doc.fileName}" wandert in den Papierkorb. Über „Gelöschte anzeigen" lässt sie sich wiederherstellen.`,
      ja: "Löschen",
      ton: "danger",
    });
    if (!ok) return;
    setDeleteErr(null);
    try {
      await documentService.remove(doc.id);
      zeileEntfernen(doc.id);
      reload();
    } catch (e) {
      setDeleteErr(e instanceof Error ? e.message : "Löschen fehlgeschlagen");
      reload();
    }
  };

  /**
   * Sammel-Löschen — Dateien UND Ordner, mit EINER Rückfrage (User
   * 22.10.2026: „mehrere ausgewählt → Löschen → nur eines weg"; zählten
   * vorher nur die markierten Dateien, blieben mitmarkierte Ordner stehen).
   *
   * Nacheinander statt parallel: das Backend prüft je Dokument (Ablage-Mails
   * lehnt es für Nicht-Admins ab), und bei einem Fehlschlag soll erkennbar
   * bleiben, was schon im Papierkorb liegt. Was in einem mitmarkierten Ordner
   * liegt, geht mit dem Ordner — nicht noch einmal einzeln.
   */
  const loescheMehrere = async ({ files: dateien, folders: ordnerAuswahl }: ExplorerAuswahl<DocumentDto>) => {
    const ordner = ordnerAuswahl.filter(
      (o) => !ordnerAuswahl.some((anderer) => anderer !== o && anderer.path !== o.path && liegtIn(o.path, anderer.path)),
    );
    const docs = dateien
      .filter((f) => !ordner.some((o) => liegtIn(f.dir, o.path)))
      .map((f) => f.payload)
      .filter((d): d is DocumentDto => !!d && !d.deleted);
    const anzahl = docs.length + ordner.length;
    if (anzahl === 0) return;
    const was =
      ordner.length === 0
        ? `${docs.length} Dateien`
        : docs.length === 0
          ? `${ordner.length} Ordner`
          : `${anzahl} Elemente`;
    const ok = await bestaetigen({
      titel: `${was} löschen?`,
      text:
        ordner.length > 0
          ? `${docs.length > 0 ? `${docs.length} ${docs.length === 1 ? "Datei" : "Dateien"} und ` : ""}${ordner.length} Ordner samt Inhalt wandern in den Papierkorb und lassen sich über „Gelöschte anzeigen" wiederherstellen.`
          : `Die Dateien wandern in den Papierkorb und lassen sich über „Gelöschte anzeigen" wiederherstellen.`,
      ja: "Löschen",
      ton: "danger",
    });
    if (!ok) return;
    setDeleteErr(null);
    const fehler: string[] = [];
    for (const d of docs) {
      try {
        await documentService.remove(d.id);
        zeileEntfernen(d.id);
      } catch (e) {
        fehler.push(`„${splitPath(d.fileName).base || d.fileName}"${e instanceof Error && e.message ? `: ${e.message}` : ""}`);
      }
    }
    let ordnerWeg = 0;
    for (const o of ordner) {
      try {
        await kundenakteOrdnerService.loeschen(contactId, o.path);
        ordnerWeg += 1;
        if (liegtIn(currentPath, o.path)) setCurrentPath(parentPath(o.path));
      } catch (e) {
        fehler.push(`Ordner „${o.name}"${e instanceof Error && e.message ? `: ${e.message}` : ""}`);
      }
    }
    if (fehler.length > 0) {
      setDeleteErr(
        fehler.length === anzahl
          ? `Nichts gelöscht — ${fehler.join("; ")}`
          : `${fehler.length} von ${anzahl} Elementen nicht gelöscht — ${fehler.join("; ")}`,
      );
    } else {
      setToast(`${anzahl} ${anzahl === 1 ? "Element liegt" : "Elemente liegen"} jetzt im Papierkorb.`);
    }
    if (ordnerWeg > 0) strukturNeuLaden();
    else reload();
  };

  /**
   * Aus dem Papierkorb zurückholen. Ohne Rückfrage — Wiederherstellen macht
   * nichts kaputt, und wer sich vertut, löscht einfach wieder.
   */
  const wiederherstellen = async (doc: DocumentDto) => {
    setDeleteErr(null);
    try {
      await documentService.restore(doc.id);
      zeileEntfernen(doc.id);
      setToast(`„${splitPath(doc.fileName).base || doc.fileName}" wiederhergestellt.`);
      reload();
    } catch (e) {
      setDeleteErr(e instanceof Error ? e.message : "Wiederherstellen fehlgeschlagen");
      reload();
    }
  };

  /**
   * Hochladen — Dateien wie ganze Ordner.
   *
   * WOHIN, entscheidet die Ansicht:
   *
   * - **Alte Kundenakte**: in den gerade geöffneten Ordner, mit dessen
   *   Kategorie. Der Explorer baut seinen Baum aus `Document.fileName`; der
   *   Pfad muss deshalb mitgeschickt werden (`relativePath`), sonst läge die
   *   Datei sichtbar an der Wurzel statt dort, wo der Nutzer sie abgelegt hat.
   *   Vorher ging das gar nicht: Die Ansicht war zu lesen, nicht zu füllen.
   *   In der eingeblendeten Akte eines verbundenen Kontakts (Haushalt,
   *   Unternehmensverbund) geht der Upload in DESSEN Akte — sie gehört ihm, und
   *   nur so steht die Datei bei allen, die diese Akte sehen (T-251: vorher
   *   landete sie unter dem unsichtbaren Anzeigepfad beim eigenen Kontakt und
   *   war nirgends zu finden). Der Server prüft den Zugriff auf den Inhaber.
   * - **KMVS Dokumente**: unverändert wie bisher — gewählte Kategorie, kein
   *   Ordner. Nur hier wird danach noch die Ansicht gewechselt, denn ein
   *   Dokument ohne die Kategorie „Alte Kundenakte" wäre im Explorer unsichtbar.
   *
   * Die eigentliche Schleife (Reihenfolge, Rate-Limit, Abbruch) liegt in
   * `documents/akteUpload.ts` und ist dort geprüft.
   */
  const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const liste = Array.from(e.target.files ?? []);
    e.target.value = "";
    void hochladen(
      liste.map((file) => ({
        file,
        // Beim Ordner-Upload liefert der Browser den Pfad innerhalb des
        // gewählten Ordners mit; bei Einzeldateien ist er leer.
        relativePath: (file as File & { webkitRelativePath?: string }).webkitRelativePath || undefined,
      })),
    );
  };

  /**
   * Gemeinsamer Weg für Auswahl-Dialog und Ziehen vom Rechner (User
   * 22.10.2026: Drag & Drop aus dem Windows-Explorer in den geöffneten
   * Ordner). `zielPfad` ist der Ordner, über dem losgelassen wurde.
   */
  const hochladen = async (dateien: AbgelegteDatei[], zielPfad: string = currentPath, gezogen = false) => {
    const liste = dateien.map((d) => d.file);
    if (!liste.length || !validContact) return;
    if (uploading) {
      setUploadErr("Es läuft noch ein Upload — bitte warten, bis er fertig ist.");
      return;
    }
    if (zeigePapierkorb) {
      setUploadErr("In den Papierkorb lässt sich nichts hochladen. Bitte „Gelöschte anzeigen“ ausschalten.");
      return;
    }
    const inAkte = mode === "explorer";
    // Der KMVS-Ordner und die Sammelwurzel der verbundenen Akten sind keine
    // Ablageorte — dort gibt es keinen Ordner, in dem die Datei später stünde.
    if (inAkte && (istKmvsPfad(zielPfad) || (istVerbundPfad(zielPfad) && !fremdeAkte(zielPfad)))) {
      setUploadErr("In diesen Ordner lässt sich nichts hochladen. Bitte einen Ordner der Kundenakte öffnen.");
      return;
    }
    const ziel = inAkte ? quelle(zielPfad) : { contactId, prefix: "" };
    setUploadErr(null);
    setUploading(true);
    uploadAbbruch.current = false;
    setUploadFortschritt({ erledigt: 0, gesamt: liste.length });
    const platzhalter = ablage.starte(liste.map((f) => ({ name: f.name, mimeType: f.type || null })));
    const auspackFehler: string[] = [];
    let ausgepackt = 0;
    // Gezogene Outlook-Mails mit Anhängen: erst nach dem ganzen Upload fragen,
    // ob die Anhänge mit abgelegt werden (User 03.11.2026) — sonst stünde der
    // Upload der übrigen Dateien so lange still.
    const mailDateien: { datei: File; dokument: Awaited<ReturnType<typeof documentService.upload>> }[] = [];
    try {
      const res = await ladeInAkteHoch({
        dateien,
        contactId: ziel.contactId,
        basisPfad: ziel.prefix,
        kategorie: inAkte ? KUNDENAKTE_CATEGORY : uploadCategory || undefined,
        onFortschritt: (erledigt, gesamt) => {
          setUploadFortschritt({ erledigt, gesamt });
          setUploadWartet(null);
        },
        onWartet: setUploadWartet,
        abbruch: () => uploadAbbruch.current,
        onDatei: (i, erg) => {
          const id = platzhalter[i];
          if (erg.ok) {
            ablage.fertig([id]);
            return;
          }
          ablage.scheitern(id, erg.nichtLesbar ? erg.grund : `Nicht hochgeladen – ${erg.grund}`, () => {
            ablage.weg([id]);
            void hochladen([dateien[i]], zielPfad, gezogen);
          });
        },
        // Gezogene Outlook-Mails (`.msg`/`.eml`): Anhänge auf Nachfrage mit
        // auspacken (User 01.11./03.11.2026) — nur beim Drag & Drop, wie bei
        // Postfach-Mails.
        nachUpload: gezogen
          ? async (datei, dokument) => {
              mailDateien.push({ datei, dokument });
            }
          : undefined,
      });
      if (mailDateien.length > 0) {
        // Die Mail selbst liegt schon — sichtbar machen, bevor gefragt wird.
        reload();
        for (const m of mailDateien) {
          const r = await nachMailDateiUploadMitFrage(m.datei, m.dokument);
          if (r?.fehler) auspackFehler.push(r.fehler);
          else if (r) ausgepackt += r.anhaenge;
        }
      }

      const fehlerZahl = res.fehler.length;
      // Abgebrochenes stand nie in der Liste — seine Platzhalter gehen mit.
      if (res.abgebrochen) ablage.weg(platzhalter.slice(res.erfolgreich + res.fehler.length));
      if (res.abgebrochen) {
        setToast(`Abgebrochen — ${res.erfolgreich} von ${liste.length} hochgeladen.`);
      } else if (fehlerZahl === 0) {
        const anhaenge = ausgepackt === 0 ? "" : ausgepackt === 1 ? " – 1 Anhang aus der Mail übernommen" : ` – ${ausgepackt} Anhänge aus der Mail übernommen`;
        setToast(
          (liste.length === 1
            ? `„${liste[0].name}" hochgeladen`
            : `${res.erfolgreich} Dateien hochgeladen`) + `${anhaenge}.`,
        );
        if (auspackFehler.length > 0) setUploadErr(auspackFehler[0]);
      } else {
        setToast(`${res.erfolgreich} von ${liste.length} hochgeladen, ${fehlerZahl} fehlgeschlagen.`);
        // Der erste Grund steht dauerhaft neben dem Knopf — der Toast ist weg,
        // bevor jemand eine Liste von 80 Dateien durchgesehen hat.
        setUploadErr(`${res.fehler[0].fileName}: ${res.fehler[0].grund}`);
      }
      if (!inAkte && res.erfolgreich > 0) switchMode("flat");
      reload();
    } catch (err) {
      setUploadErr(err instanceof Error ? err.message : "Upload fehlgeschlagen");
      ablage.weg(platzhalter);
    } finally {
      setUploading(false);
      setUploadFortschritt(null);
      setUploadWartet(null);
    }
  };

  /**
   * Mail oder Mail-Anhänge aus dem Postfach auf die Liste gezogen (User
   * 01.11.2026) — auch aus einem anderen Browserfenster. Abgelegt wird beim
   * Kontakt (KMVS-Dokumente), nicht im geöffneten Ordner der Alten Kundenakte:
   * Der Server legt beides als Kundendokument ab.
   */
  const ablegenAusPostfach = async (dt: DataTransfer) => {
    // Synchron lesen — nach dem ersten `await` ist der Inhalt weg.
    // Neues Outlook: nur EWS-Ids, die erst der Server übersetzt (s. `neuesOutlook.ts`).
    const outlook = ausNeuemOutlook(dt);
    const zug = outlook ? null : leseAblageZug(dt);
    if ((!zug && !outlook) || !validContact) return;
    // Dort sichtbar, wo abgelegt wurde (User 01.11.2026): in der Alten
    // Kundenakte im offenen Ordner, sonst unter „KMVS Dokumente“ mit der
    // gewählten Kategorie — dieselbe Regel wie beim Datei-Upload.
    const inAkte = mode === "explorer";
    if (inAkte && (istKmvsPfad(currentPath) || (istVerbundPfad(currentPath) && !fremdeAkte(currentPath)))) {
      setUploadErr("In diesen Ordner lässt sich nichts ablegen. Bitte einen Ordner der Kundenakte öffnen.");
      return;
    }
    const zielAkte = inAkte ? quelle(currentPath) : { contactId, prefix: "" };
    const ort = {
      kategorie: inAkte ? KUNDENAKTE_CATEGORY : uploadCategory || null,
      ordner: inAkte ? zielAkte.prefix.replace(/^\/+|\/+$/g, "") || null : null,
    };
    setUploadErr(null);
    // Mehrere Mails nacheinander ziehen geht ohne Warten: Jede bekommt ihren
    // Platzhalter, die Liste lädt nach jeder Antwort neu.
    // Mail mit Anhängen: vorher fragen, was abgelegt wird (User 03.11.2026).
    const ziel = { contactId: zielAkte.contactId, ort };
    const ergebnis = outlook
        ? await legeNeuesOutlookAb(outlook, (z) => legeZugAbMitAnhangFrage(z, ziel, ablage, reload))
        : zug
          ? await legeZugAbMitAnhangFrage(zug, ziel, ablage, reload)
          : null;
    if (ergebnis && ergebnis.fehlgeschlagen.length === 0) setToast(ergebnis.meldung);
  };

  const saveCategory = async () => {
    if (!catEdit) return;
    setCatSaving(true);
    setCatErr(null);
    try {
      // Leere Auswahl = Kategorie entfernen: das Backend macht aus einem leeren
      // String null (`input.category?.trim() || null`).
      const neu = await documentService.update(catEdit.doc.id, { category: catEdit.value || null });
      zeileErsetzen(neu);
      setCatEdit(null);
      reload();
    } catch (err) {
      setCatErr(err instanceof Error ? err.message : "Speichern fehlgeschlagen");
      reload();
    } finally {
      setCatSaving(false);
    }
  };

  const setTreeWidthPersisted = (px: number) => {
    setTreeWidth(px);
    try {
      localStorage.setItem(TREE_WIDTH_KEY, String(px));
    } catch {
      /* Private-Mode o. Ä. — die Breite gilt dann nur für diese Sitzung. */
    }
  };

  // Memoisiert, weil der FileExplorer daraus seine Zeilen- und Auswahl-Keys
  // ableitet: frische Arrays bei jedem Render würden dort Effects und Memos
  // unnötig neu auslösen. MUSS vor dem frühen Return stehen (Hook-Regeln).
  const kmvs = React.useMemo(() => baueKmvsStruktur(kmvsRows), [kmvsRows]);
  const imKmvs = explorerBrowse && istKmvsPfad(currentPath);
  const files = React.useMemo(
    () => (imKmvs ? kmvs.dateien(currentPath) : rows).map(toExplorerFile),
    [imKmvs, kmvs, currentPath, rows],
  );
  // Leere, selbst angelegte Ordner nur im Normalbetrieb — im Papierkorb geht
  // es um gelöschte Dateien, nicht um Ordner ohne Inhalt.
  const angelegtSichtbar = zeigePapierkorb ? EMPTY_PFADE : angelegt;
  /** Unterordner einer Ebene: Server + selbst angelegte, an der Wurzel „KMVS" davor. */
  const verbundOrdner = React.useMemo<ExplorerFolder[]>(
    () =>
      verbundAkten.map((k) => ({
        kind: "folder" as const,
        name: verbundOrdnerName(k),
        path: verbundPfad(k.id),
        count: k.anzahl,
        reihenfolge: 0,
        virtuell: true,
      })),
    [verbundAkten],
  );
  const ordnerAuf = React.useCallback(
    (pfad: string): ExplorerFolder[] | undefined => {
      if (istKmvsPfad(pfad)) return kmvs.ordner(pfad);
      // Fremde Akte: nur, was der Server dort findet — keine eigenen leeren Ordner.
      if (istVerbundPfad(pfad)) return folderCache[pfad];
      const server = folderCache[pfad];
      if (server === undefined && pfad !== "") return undefined;
      const mit = mitAngelegtenOrdnern(server ?? EMPTY_FOLDERS, angelegtSichtbar, pfad);
      return pfad === "" && !nurAlteAkte ? [...kmvs.ordner(""), ...verbundOrdner, ...mit] : mit;
    },
    [kmvs, folderCache, angelegtSichtbar, nurAlteAkte, verbundOrdner],
  );

  if (!validContact) {
    return <div className={styles.empty}>Bitte zuerst den Kontakt speichern, um Dokumente anzuzeigen.</div>;
  }

  const folders = explorerBrowse ? ordnerAuf(currentPath) ?? EMPTY_FOLDERS : EMPTY_FOLDERS;

  /**
   * Leere Akte: kein alter Ordner, keine Datei, kein selbst angelegter Ordner —
   * höchstens KMVS-Dokumente. Dann steht unter der Liste der Weg, eine alte
   * Kundenakte zuzuweisen (PO 28.09.2026).
   */
  const akteLeer =
    !nurAlteAkte &&
    mode === "explorer" &&
    explorerBrowse &&
    currentPath === "" &&
    !zeigePapierkorb &&
    !loading &&
    folderCache[""] !== undefined &&
    folderCache[""].length === 0 &&
    rows.length === 0 &&
    angelegt.length === 0;
  // Eine Zuweisung, deren Import noch läuft oder gescheitert ist — dann statt
  // des Knopfs der Stand.
  const offeneZuweisung = (zuweisungen ?? []).find((z) => {
    const eigene = z.contact?.id === contactId ? z : null;
    const weitere = z.weitereKontakte?.find((w) => w.contactId === contactId);
    return eigene ? !eigene.importedAt || !!eigene.importError : weitere ? !weitere.importedAt || !!weitere.importError : false;
  });
  const importFehler =
    offeneZuweisung &&
    (offeneZuweisung.contact?.id === contactId
      ? offeneZuweisung.importError
      : offeneZuweisung.weitereKontakte?.find((w) => w.contactId === contactId)?.importError);

  /** Nach jeder Änderung an Ordnern: Baum neu aufbauen (Pfade haben sich verschoben). */
  const strukturNeuLaden = () => {
    // Sichtbare Ebenen bleiben stehen, bis ihre neue Fassung da ist — verworfen
    // wird nur, was zugeklappt ist und beim Aufklappen frisch geladen werden muss.
    setFolderCache((prev) => {
      const sichtbar = new Set(["", currentPath, ...expandedPaths]);
      return Object.fromEntries(Object.entries(prev).filter(([p]) => sichtbar.has(p)));
    });
    setStrukturKey((k) => k + 1);
    reload();
    for (const p of new Set(["", ...expandedPaths])) void loadFolderLevel(p);
  };

  const nameSpeichern = async () => {
    if (!nameDialog) return;
    const name = nameDialog.name.trim();
    if (!name) {
      setNameErr("Bitte einen Namen eingeben.");
      return;
    }
    if (UNGUELTIGE_ZEICHEN.test(name) || name === "." || name === "..") {
      setNameErr('Ein Ordnername darf keines dieser Zeichen enthalten: \\ / : * ? " < > |');
      return;
    }
    const eltern = nameDialog.art === "neu" ? nameDialog.eltern : parentPath(nameDialog.alt.path);
    const ziel = normalizeFolderPath(eltern + name);
    const geschwister = ordnerAuf(eltern) ?? [];
    const belegt = geschwister.some(
      (f) =>
        f.name.toLocaleLowerCase("de") === name.toLocaleLowerCase("de") &&
        (nameDialog.art === "neu" || f.path !== nameDialog.alt.path),
    );
    if (belegt) {
      setNameErr(`In diesem Ordner gibt es schon „${name}".`);
      return;
    }
    setNameBusy(true);
    setNameErr(null);
    try {
      if (nameDialog.art === "neu") {
        await kundenakteOrdnerService.anlegen(contactId, ziel);
        // Aus dem Baum angelegt: den Elternordner aufklappen, sonst sieht man
        // den neuen Ordner nicht.
        if (eltern) setExpandedPaths((prev) => new Set(prev).add(eltern));
      } else {
        const alt = nameDialog.alt.path;
        await kundenakteOrdnerService.verschieben(contactId, alt, ziel);
        // Steht man gerade in dem umbenannten Ordner, zieht die Ansicht mit.
        if (liegtIn(currentPath, alt)) setCurrentPath(ziel + currentPath.slice(alt.length));
      }
      setNameDialog(null);
      strukturNeuLaden();
    } catch (e) {
      setNameErr(e instanceof Error ? e.message : "Speichern fehlgeschlagen");
    } finally {
      setNameBusy(false);
    }
  };

  const ordnerLoeschenBestaetigt = async () => {
    const f = ordnerLoeschen;
    setOrdnerLoeschen(null);
    if (!f) return;
    try {
      const { dateien } = await kundenakteOrdnerService.loeschen(contactId, f.path);
      setToast(
        dateien > 0
          ? `„${f.name}" gelöscht — ${dateien} ${dateien === 1 ? "Datei liegt" : "Dateien liegen"} im Papierkorb.`
          : `„${f.name}" gelöscht.`,
      );
      if (liegtIn(currentPath, f.path)) setCurrentPath(parentPath(f.path));
      strukturNeuLaden();
    } catch (e) {
      setDeleteErr(e instanceof Error ? e.message : "Löschen fehlgeschlagen");
    }
  };

  /**
   * Neue, leere Office-Datei im geöffneten Ordner — und gleich im Editor
   * öffnen (PO 28.09.2026: „neue Datei → Office-Auswahl"). Wie Windows legt
   * sie einen freien Namen an; umbenennen geht danach per Rechtsklick.
   */
  const [neueOfficeDatei, setNeueOfficeDatei] = React.useState<{ art: NeueDateiArt; pfad: string } | null>(null);
  const neueDatei = (art: NeueDateiArt, pfad: string = currentPath) => {
    setNeueOfficeDatei({ art, pfad });
  };
  const neueDateiAnlegen = async (art: NeueDateiArt, pfad: string): Promise<number> => {
    const v = NEUE_DATEI[art];
    try {
      const res = await fetch(`${import.meta.env.BASE_URL ?? "/"}vorlagen/leer.${art}`);
      if (!res.ok) throw new Error("Vorlage nicht gefunden");
      // Im geöffneten Ordner kennt die Liste die Namen schon; für einen Ordner
      // aus dem Baum oder der Liste werden sie kurz nachgeschlagen.
      const belegt =
        pfad === currentPath
          ? files.map((f) => f.name)
          : (
              await documentService.listPaged({
                contactId,
                category: KUNDENAKTE_CATEGORY,
                pathPrefix: pfad,
                limit: 1000,
              })
            ).items.map((d) => splitPath(d.fileName).base);
      const name = freierName(v.name, belegt, `.${art}`);
      const file = new File([await res.blob()], name, { type: v.mime });
      const doc = await documentService.upload({
        file,
        contactId,
        category: KUNDENAKTE_CATEGORY,
        ...(pfad ? { relativePath: pfad } : {}),
      });
      if (pfad === currentPath) reload();
      else strukturNeuLaden();
      return doc.id;
    } catch (e) {
      setUploadErr(e instanceof Error ? e.message : "Datei konnte nicht angelegt werden");
      throw e;
    }
  };

  /** Verschieben per Drag & Drop: Dateien bekommen einen neuen Ordner, Ordner einen neuen Pfad. */
  const verschieben = async (
    drag: { files: ExplorerFile<DocumentDto>[]; folders: ExplorerFolder[] },
    ziel: string,
  ) => {
    const zielPfad = normalizeFolderPath(ziel);
    let ok = 0;
    let fehler: string | null = null;
    for (const f of drag.folders) {
      try {
        await kundenakteOrdnerService.verschieben(contactId, f.path, zielPfad + f.name + "/");
        ok += 1;
      } catch (e) {
        fehler = e instanceof Error ? e.message : "Verschieben fehlgeschlagen";
      }
    }
    for (const f of drag.files) {
      if (!f.payload) continue;
      try {
        await documentService.update(f.payload.id, { ordner: zielPfad });
        ok += 1;
      } catch {
        fehler = "Mindestens eine Datei konnte nicht verschoben werden.";
      }
    }
    if (fehler) setDeleteErr(fehler);
    if (ok > 0) {
      const zielName = zielPfad ? zielPfad.slice(0, -1).split("/").pop() : WURZEL_LABEL;
      setToast(ok === 1 ? `Nach „${zielName}" verschoben.` : `${ok} Elemente nach „${zielName}" verschoben.`);
    }
    strukturNeuLaden();
  };

  /**
   * Ganzen Ordner als ZIP herunterladen, mit allen Unterordnern (PO 28.09.2026:
   * Rechtsklick → „herunterladen"). Die Dateien holt der Browser einzeln und
   * packt sie selbst — der Server braucht dafür nichts Neues außer dem
   * rekursiven Filter `pathUnder`.
   */
  const ordnerHerunterladen = async (f: ExplorerFolder) => {
    const q = quelle(f.path);
    try {
      setToast(`„${f.name}" wird zusammengestellt …`);
      const docs: DocumentDto[] = [];
      for (let off = 0; ; off += ZIP_SEITE) {
        const { items } = await documentService.listPaged({
          contactId: q.contactId,
          category: KUNDENAKTE_CATEGORY,
          pathUnder: q.prefix,
          limit: ZIP_SEITE,
          offset: off,
          sort: "name",
          dir: "asc",
        });
        docs.push(...items);
        if (items.length < ZIP_SEITE || docs.length > ZIP_MAX_DATEIEN) break;
      }
      if (!docs.length) {
        setToast(`„${f.name}" enthält keine Dateien.`);
        return;
      }
      const bytes = docs.reduce((n, d) => n + (d.fileSize ?? 0), 0);
      if (docs.length > ZIP_MAX_DATEIEN || bytes > ZIP_MAX_BYTES) {
        setToast(null);
        setDeleteErr(
          `„${f.name}" ist zu groß für einen Download am Stück (${docs.length} Dateien, ` +
            `${Math.round(bytes / 1024 / 1024)} MB). Bitte einen Unterordner herunterladen.`,
        );
        return;
      }
      const eintraege: ZipEintrag[] = [];
      let fehler = 0;
      let erledigt = 0;
      // Vier Abrufe gleichzeitig — schneller als einzeln, ohne das Rate-Limit zu reizen.
      const warteschlange = [...docs];
      const arbeiter = async () => {
        for (let d = warteschlange.shift(); d; d = warteschlange.shift()) {
          try {
            const blob = await documentService.fetchBlob(d.id);
            eintraege.push({
              name: d.fileName.slice(q.prefix.length),
              daten: new Uint8Array(await blob.arrayBuffer()),
              geaendert: d.uploadedAt ? new Date(d.uploadedAt) : undefined,
            });
          } catch {
            fehler += 1;
          }
          erledigt += 1;
          if (erledigt % 10 === 0) setToast(`„${f.name}": ${erledigt} von ${docs.length} Dateien geladen …`);
        }
      };
      await Promise.all([arbeiter(), arbeiter(), arbeiter(), arbeiter()]);
      eintraege.sort((a, b) => a.name.localeCompare(b.name, "de"));
      const namen = eindeutigeNamen(eintraege.map((e) => e.name));
      triggerBlobDownload(baueZip(eintraege.map((e, i) => ({ ...e, name: namen[i] }))), `${f.name}.zip`);
      setToast(
        fehler === 0
          ? `„${f.name}.zip" heruntergeladen (${eintraege.length} Dateien).`
          : `„${f.name}.zip" heruntergeladen — ${fehler} von ${docs.length} Dateien fehlen darin.`,
      );
    } catch {
      setToast(null);
      setDeleteErr(`„${f.name}" konnte nicht heruntergeladen werden.`);
    }
  };

  /** „Verschieben nach…" per Rechtsklick: was verschoben wird, solange der Zielordner-Dialog offen ist. */
  const [verschiebeAuswahl, setVerschiebeAuswahl] = React.useState<{
    files: ExplorerFile<DocumentDto>[];
    folders: ExplorerFolder[];
  } | null>(null);

  /** Darf in diesen Ordner etwas geschrieben werden? Nur in der echten Akte. */
  const beschreibbarIn = (pfad: string) =>
    mode === "explorer" && explorerBrowse && !zeigePapierkorb && !istKmvsPfad(pfad) && !istVerbundPfad(pfad);
  const beschreibbar = beschreibbarIn(currentPath);
  // Wohin ein Upload geht, in Worten: in der eingeblendeten Akte eines
  // verbundenen Kontakts steht dessen Name davor, nie der interne Anzeigepfad.
  const uploadZielName = (() => {
    const f = fremdeAkte(currentPath);
    if (!f) return currentPath || WURZEL_LABEL;
    const k = verbundAkten.find((v) => v.id === f.id);
    const akte = k ? verbundOrdnerName(k) : `Kontakt ${f.id}`;
    const innen = f.innen.replace(/\/+$/, "");
    return innen ? `${akte} / ${innen}` : akte;
  })();

  /** Neuer Ordner / neue Dateien / Hochladen — gemeinsam für freie Fläche, Baum und Ordnermenü. */
  const anlegenEintraege = (pfad: string): RdMenuItem[] => [
    {
      id: "neuer-ordner",
      label: "Neuer Ordner…",
      icon: "▣",
      onSelect: () => {
        setNameErr(null);
        setNameDialog({
          art: "neu",
          eltern: pfad,
          name: freierName("Neuer Ordner", (ordnerAuf(pfad) ?? []).map((f) => f.name)),
        });
      },
    },
    ...(Object.keys(NEUE_DATEI) as NeueDateiArt[]).map((art) => ({
      id: `neu-${art}`,
      label: NEUE_DATEI[art].name,
      icon: art === "docx" ? "W" : art === "xlsx" ? "X" : "P",
      onSelect: () => void neueDatei(art, pfad),
    })),
    { kind: "sep" },
    // Der Upload landet immer im geöffneten Ordner — also erst dorthin wechseln.
    {
      id: "hochladen",
      label: "Dateien hochladen…",
      icon: "⤒",
      onSelect: () => {
        if (pfad !== currentPath) navigate(pfad);
        fileRef.current?.click();
      },
    },
    {
      id: "ordner-hochladen",
      label: "Ordner hochladen…",
      icon: "⤒",
      onSelect: () => {
        if (pfad !== currentPath) navigate(pfad);
        setToast("Ordner auswählen — der Browser fragt danach noch einmal nach; bitte bestätigen.");
        ordnerRef.current?.click();
      },
    },
  ];

  /** Menü der freien Fläche — und der Wurzel im Baum (`pfad` = ""). */
  const hintergrundMenue = (pfad: string = currentPath): RdMenuItem[] => {
    if (!beschreibbarIn(pfad)) return [];
    const items: RdMenuItem[] = anlegenEintraege(pfad);
    if (akteLeer && !offeneZuweisung && pfad === currentPath) {
      items.push({ kind: "sep" }, {
        id: "akte-zuweisen",
        label: "Alte Kundenakte zuweisen…",
        icon: "⇲",
        onSelect: () => setZuweisenOffen(true),
      });
    }
    items.push({ kind: "sep" }, { id: "aktualisieren", label: "Aktualisieren", icon: "↻", onSelect: strukturNeuLaden });
    return items;
  };

  /**
   * Menü eines Ordners — in der Liste wie im Baum links (PO 28.09.2026:
   * „umbenennen, löschen, neue Datei, herunterladen und neuer Ordner").
   */
  /** Darf dieser Ordner gelöscht werden? Dieselbe Regel wie im Ordnermenü. */
  const ordnerLoeschbar = (f: ExplorerFolder): boolean =>
    !zeigePapierkorb && explorerBrowse && !istKmvsPfad(f.path) && beschreibbarIn(f.path) && !f.virtuell;

  const ordnerMenue = (f: ExplorerFolder): RdMenuItem[] => {
    const items: RdMenuItem[] = [{ id: "open", label: "Öffnen", icon: "▸", onSelect: () => navigate(f.path) }];
    if (zeigePapierkorb || !explorerBrowse || istKmvsPfad(f.path)) return items;
    // Herunterladen geht auch in der Akte eines verbundenen Kontakts — nur
    // nicht für deren Wurzel als Ganzes.
    const herunterladen: RdMenuItem = {
      id: "download",
      label: "Herunterladen (ZIP)",
      icon: "⤓",
      onSelect: () => void ordnerHerunterladen(f),
    };
    if (!beschreibbarIn(f.path)) {
      if (quelle(f.path).prefix) items.push({ kind: "sep" }, herunterladen);
      return items;
    }
    items.push({ kind: "sep" }, ...anlegenEintraege(f.path), { kind: "sep" }, herunterladen);
    if (f.virtuell) return items;
    items.push(
      { kind: "sep" },
      {
        id: "rename",
        label: "Umbenennen…",
        icon: "✎",
        onSelect: () => {
          setNameErr(null);
          setNameDialog({ art: "umbenennen", alt: f, name: f.name });
        },
      },
      {
        id: "move",
        label: "Verschieben nach…",
        icon: "➜",
        onSelect: () => setVerschiebeAuswahl({ files: [], folders: [f] }),
      },
      { kind: "sep" },
      { id: "delete", label: "Löschen", icon: "🗑", gefahr: true, onSelect: () => setOrdnerLoeschen(f) },
    );
    return items;
  };

  const leereAkte = akteLeer ? (
    <div className="docx-akte-leer">
      {offeneZuweisung && !importFehler ? (
        <>
          <b>Alte Kundenakte wird importiert</b>
          <span>
            „{offeneZuweisung.folderName}" ist diesem Kontakt zugewiesen. Die Dateien erscheinen hier in
            einigen Minuten.
          </span>
          <button type="button" className="docx-seg-btn" onClick={strukturNeuLaden}>
            Aktualisieren
          </button>
        </>
      ) : (
        <>
          <b>Noch keine alte Kundenakte</b>
          <span>
            {importFehler
              ? `Der Import von „${offeneZuweisung?.folderName}" ist fehlgeschlagen: ${importFehler}`
              : "Dateien hierher hochladen, einen Ordner anlegen (Rechtsklick) oder den passenden Ordner vom Netzlaufwerk zuweisen."}
          </span>
          <button type="button" className="docx-akte-leer-btn" onClick={() => setZuweisenOffen(true)}>
            Alte Kundenakte hier zuweisen
          </button>
        </>
      )}
    </div>
  ) : null;

  const toolbar = (
    <>
      <label className="docx-search">
        <Search16Regular />
        <input
          type="search"
          placeholder="Dokumente durchsuchen…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Dokumente durchsuchen"
        />
      </label>
      <div className="docx-seg" role="group" aria-label="Dokumentbestand">
        <button
          type="button"
          className={"docx-seg-btn" + (mode === "explorer" ? " on" : "")}
          onClick={() => switchMode("explorer")}
          aria-pressed={mode === "explorer"}
        >
          <Folder16Regular />
          Alte Kundenakte
        </button>
        <button
          type="button"
          className={"docx-seg-btn" + (mode === "flat" ? " on" : "")}
          onClick={() => switchMode("flat")}
          aria-pressed={mode === "flat"}
        >
          <Apps16Regular />
          KMVS Dokumente
        </button>
      </div>
      <input ref={fileRef} type="file" multiple style={{ display: "none" }} onChange={onPickFiles} />
      {/* Ordner-Upload. `webkitdirectory` steht in keiner Norm, wird aber von
          Chrome, Edge, Firefox und Safari unterstützt — React kennt das
          Attribut nur nicht in seinen Typen. */}
      <input
        ref={ordnerRef}
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={onPickFiles}
        {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
      />
      {/* In der Alten Kundenakte ist die Kategorie fest — dort steht statt der
          Auswahl der Zielordner. */}
      {mode === "flat" ? (
        <select
          className={styles.catSelect}
          value={uploadCategory}
          onChange={(e) => setUploadCategory(e.target.value)}
          disabled={uploading}
          aria-label="Kategorie des Uploads"
          title="Kategorie, mit der die nächste Datei abgelegt wird"
        >
          <option value="">Ohne Kategorie</option>
          {DOKUMENT_KATEGORIEN.map((k) => (
            <option key={k.value} value={k.value}>
              {k.value}
            </option>
          ))}
        </select>
      ) : null}
      {/* Papierkorb (PO 16.09.2026). Dieselbe Optik wie der Modus-Umschalter
          darüber: ein `docx-seg` mit einem Knopf, `aria-pressed` + `.on`. */}
      <div className="docx-seg">
        <button
          type="button"
          className={"docx-seg-btn" + (zeigePapierkorb ? " on" : "")}
          onClick={() => setZeigePapierkorb((v) => !v)}
          aria-pressed={zeigePapierkorb}
          title="Gelöschte Dokumente ansehen und wiederherstellen"
        >
          {I.recycle}
          Gelöschte anzeigen
        </button>
      </div>
      <div className="docx-seg">
        <button
          type="button"
          className="docx-seg-btn"
          disabled={uploading || zeigePapierkorb}
          onClick={() => fileRef.current?.click()}
          title={
            mode === "explorer"
              ? `Dateien nach „${uploadZielName}" hochladen`
              : "Dateien hochladen"
          }
        >
          <ArrowUpload16Regular />
          {uploading && uploadFortschritt
            ? fortschrittText(uploadFortschritt.erledigt, uploadFortschritt.gesamt)
            : "Hochladen"}
        </button>
        {/* Ein ganzer Ordner ergibt nur in der Akte Sinn: nur dort werden
            Ordner überhaupt dargestellt. */}
        {mode === "explorer" ? (
          <button
            type="button"
            className="docx-seg-btn"
            disabled={uploading || zeigePapierkorb}
            onClick={() => {
              // Der Browser fragt beim Ordner-Upload zusätzlich nach — in Edge
              // leise oben am Fensterrand. Wer das nicht erwartet, hält den
              // Upload für kaputt.
              setToast("Ordner auswählen — der Browser fragt danach noch einmal nach; bitte bestätigen.");
              ordnerRef.current?.click();
            }}
            title={`Einen ganzen Ordner nach „${uploadZielName}" hochladen`}
          >
            <Folder16Regular />
            Ordner hochladen
          </button>
        ) : null}
      </div>
      {uploading ? (
        <div className="docx-seg">
          <button type="button" className="docx-seg-btn" onClick={() => { uploadAbbruch.current = true; }}>
            Abbrechen
          </button>
        </div>
      ) : null}
      {uploadWartet != null ? (
        <span className="docx-count" role="status">
          Der Server bremst kurz — weiter in {uploadWartet} s
        </span>
      ) : null}
      {uploadErr ? <span className="docx-err">{uploadErr}</span> : null}
      {deleteErr ? (
        <span className="docx-err" role="alert">
          {deleteErr}
        </span>
      ) : null}
      {/* Beim Ordner-Browsing zählt der Server nur die AKTUELLE Ebene — an der
          Wurzel stünde sonst „0 Dokumente" neben Ordnern mit vierstelligen
          Zahlen. Das Label sagt deshalb dazu, worauf sich die Zahl bezieht. */}
      <span className="docx-count">
        {zeigePapierkorb
          ? `${total.toLocaleString("de-DE")} gelöschte${total === 1 ? "s Dokument" : " Dokumente"}`
          : imKmvs
            ? // Der Ordner „KMVS" zeigt höchstens KMVS_DOKUMENTE_LIMIT — liegt
              // mehr vor, sagt das Label, wo der Rest steht.
              kmvsTotal > kmvsRows.length
              ? `${files.length.toLocaleString("de-DE")} ${files.length === 1 ? "Datei" : "Dateien"} in diesem Ordner — ${(kmvsTotal - kmvsRows.length).toLocaleString("de-DE")} weitere nur unter „KMVS Dokumente"`
              : `${files.length.toLocaleString("de-DE")} ${files.length === 1 ? "Datei" : "Dateien"} in diesem Ordner`
          : explorerBrowse
            ? ordnerZaehlerLabel({ total, wurzel: currentPath === "", vorgangTotal: 0, vorgangGeladen: 0 })
            : `${total.toLocaleString("de-DE")} ${total === 1 ? "Dokument" : "Dokumente"} ${
                mode === "explorer" ? "(Alte Kundenakten)" : "(KMVS)"
              }`}
      </span>
    </>
  );

  return (
    <>
      <FileExplorer<DocumentDto>
        rootLabel={WURZEL_LABEL}
        path={currentPath}
        onNavigate={navigate}
        folders={folders}
        files={files}
        browsing={explorerBrowse}
        treeChildren={ordnerAuf}
        expandedPaths={expandedPaths}
        onToggleExpand={onToggleExpand}
        treeWidth={treeWidth}
        onTreeWidthChange={setTreeWidthPersisted}
        loading={loading}
        hasMore={hasMore}
        onLoadMore={fetchMore}
        scrollContainerId={scrollId}
        toolbar={toolbar}
        showCategory={!explorerBrowse || imKmvs}
        segmentLabel={(p) => {
          if (istKmvsPfad(p)) return kmvs.name(p);
          if (p === VERBUND_WURZEL) return "";
          const f = fremdeAkte(p);
          if (!f) return undefined;
          if (f.innen) return undefined;
          const k = verbundAkten.find((v) => v.id === f.id);
          return k ? verbundOrdnerName(k) : `Kontakt ${f.id}`;
        }}
        folderMenuItems={ordnerMenue}
        backgroundMenuItems={hintergrundMenue}
        belowList={leereAkte}
        // Auch wenn gerade der KMVS-Ordner offen ist: gezogen werden kann dann
        // im Baum links. Was wohin darf, regeln canDrag und canDropInto.
        onMove={
          mode === "explorer" && explorerBrowse && !zeigePapierkorb
            ? (drag, ziel) => void verschieben(drag, ziel)
            : undefined
        }
        onMoveRequest={
          mode === "explorer" && explorerBrowse && !zeigePapierkorb
            ? (files) => setVerschiebeAuswahl({ files, folders: [] })
            : undefined
        }
        canDrag={(row) =>
          row.kind === "folder"
            ? !row.virtuell && !istVerbundPfad(row.path)
            : row.payload?.category === KUNDENAKTE_CATEGORY &&
              !row.payload?.deleted &&
              // Nur Dateien der eigenen Akte — eine fremde gehört ihrem Inhaber.
              (row.payload?.contactId == null || row.payload.contactId === contactId)
        }
        canDropInto={(p) => !istKmvsPfad(p) && !istVerbundPfad(p)}
        emptyText={
          loading
            ? "Lade Dokumente…"
            : explorerBrowse
              ? imKmvs
                ? "In diesem Ordner liegt nichts."
                : "Dieser Ordner ist leer."
              : mode === "explorer"
                ? "Keine Treffer in den Alten Kundenakten."
                : "Keine Dokumente vorhanden."
        }
        onOpenFile={(f) => f.payload && void openPreview(f.payload)}
        onDownload={(f) => f.payload && void download(f.payload)}
        onRename={(f) => f.payload && openRename(f.payload)}
        onSetCategory={(f) => {
          if (!f.payload) return;
          setCatErr(null);
          setCatEdit({ doc: f.payload, value: f.payload.category ?? "" });
        }}
        onBulkDownload={requestBulkDownload}
        onDelete={(f) => f.payload && void loeschen(f.payload)}
        onBulkDelete={(auswahl) => void loescheMehrere(auswahl)}
        // Dateien vom Rechner auf die Liste ziehen → Upload in den geöffneten
        // Ordner (bzw. unter „KMVS Dokumente" mit der gewählten Kategorie).
        onDropFiles={zeigePapierkorb ? undefined : (dateien, ziel) => void hochladen(dateien, ziel, true)}
        fremdAblageHinweis={(typen) => {
          const art = fremdAblageArt(typen);
          if (!art) return null;
          const was = fremdAblageWas(art);
          // Abgelegt wird dort, wo die Liste gerade steht (s. ablegenAusPostfach).
          return mode === "explorer"
            ? `${was} nach „${uploadZielName}" ablegen`
            : `${was} beim Kontakt ablegen (KMVS-Dokumente)`;
        }}
        onDropFremd={zeigePapierkorb || !validContact ? undefined : (dt) => void ablegenAusPostfach(dt)}
        kopfZeilen={(spalten) => (
          <AblagePlatzhalterZeilen
            spalten={spalten}
            eintraege={ablage.eintraege}
            onErneut={ablage.erneutVersuchen}
            kannErneut={ablage.kannErneut}
            onEntfernen={(id) => ablage.weg([id])}
          />
        )}
        dropFilesHinweis={
          mode === "explorer"
            ? `Dateien und Ordner nach „${uploadZielName}" hochladen`
            : uploadCategory
              ? `Dateien hochladen — Kategorie „${uploadCategory}"`
              : "Dateien hochladen"
        }
        // Ordner löschen dürfen nur echte Ordner der eigenen Akte — dieselbe
        // Regel wie der Eintrag „Löschen" im Ordnermenü.
        canDeleteFolder={ordnerLoeschbar}
        // Ins Mailfenster ziehen (User 22.10.2026): markierte Dateien hängen
        // dort als KMVS-Dokument an. Ordner lassen sich nicht anhängen; das
        // Mailfenster sagt das, statt sie still zu verschlucken.
        onDragOut={(auswahl, dt) => {
          const docs = auswahl.files
            .map((f) => f.payload)
            .filter((d): d is DocumentDto => !!d && !d.deleted);
          if (docs.length === 0 && auswahl.folders.length === 0) return false;
          dt.setData(ZIEHTYP_DOKUMENT, dokumentZuegeNutzlast(docs, auswahl.folders.length));
          return true;
        }}
        canDragOut={(f) => !!f.payload && !f.payload.deleted}
        // Papierkorb: nur im Modus „Gelöschte anzeigen" mitgeben — sonst stünde
        // der Menüpunkt an Zeilen, an denen er nichts zu suchen hat.
        onRestore={
          zeigePapierkorb ? (f) => f.payload && void wiederherstellen(f.payload) : undefined
        }
        // P6: Chip „aus Vorgang …" springt in die Vorgangsansicht (V4).
        onTagClick={(f) => {
          const v = f.payload?.vorgang;
          if (v) routerNavigate(`/vorgaenge/v4/${v.id}`);
        }}
      />

      {neueOfficeDatei && <OfficeOpenDialog
        createDocument={() => neueDateiAnlegen(neueOfficeDatei.art, neueOfficeDatei.pfad)}
        onOpened={() => {}}
        onDismiss={() => setNeueOfficeDatei(null)}
      />}
      {rename && (
        <NamenEingabeDialog
          titel="Dokument umbenennen"
          feldName="Neuer Dateiname"
          platzhalter="Dateiname"
          wert={rename.name}
          onWert={(name) => setRename((r) => (r ? { ...r, name } : r))}
          endung={rename.ext || undefined}
          ort={rename.doc.fileName.includes("/") ? `Ordner: ${splitPath(rename.doc.fileName).dir}` : undefined}
          hinweis={
            hatSchraegstrich(rename.name) ? (
              <SchraegstrichHinweis ergebnis={dateinameOhneSchraegstrich(rename.name.trim()) + rename.ext} />
            ) : undefined
          }
          fehler={renameErr}
          busy={renaming}
          onSpeichern={() => void saveRename()}
          onAbbrechen={closeRename}
        />
      )}

      {catEdit && (
        <Dialog open onOpenChange={(_, d) => { if (!d.open) setCatEdit(null); }}>
          <DialogSurface style={{ maxWidth: "90vw", width: "480px" }}>
            <DialogBody>
              <DialogTitle>Kategorie zuordnen</DialogTitle>
              <div className={styles.renameBody}>
                <span className={styles.dirHint}>{splitPath(catEdit.doc.fileName).base}</span>
                <select
                  className={styles.catSelect}
                  value={catEdit.value}
                  onChange={(e) => setCatEdit((c) => (c ? { ...c, value: e.target.value } : c))}
                  disabled={catSaving}
                  aria-label="Kategorie"
                  autoFocus
                >
                  <option value="">Ohne Kategorie</option>
                  {DOKUMENT_KATEGORIEN.map((k) => (
                    <option key={k.value} value={k.value}>
                      {k.hinweis ? `${k.value} — ${k.hinweis}` : k.value}
                    </option>
                  ))}
                  {/* Maschinell gesetzte Kategorien („Alte Kundenakte", „Schaden",
                      „E-Mail-Anhang") stehen nicht zur Wahl, dürfen an einem
                      Bestandsdokument aber auch nicht unbemerkt verschwinden. */}
                  {catEdit.value && !DOKUMENT_KATEGORIEN.some((k) => k.value === catEdit.value) && (
                    <option value={catEdit.value}>{catEdit.value} (bestehend)</option>
                  )}
                </select>
                {catErr && <div className={styles.uploadErr}>{catErr}</div>}
              </div>
              <DialogActions>
                <Button appearance="secondary" onClick={() => setCatEdit(null)} disabled={catSaving}>
                  Abbrechen
                </Button>
                <Button appearance="primary" onClick={saveCategory} disabled={catSaving}>
                  {catSaving ? "Speichert…" : "Speichern"}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}

      <ConfirmDialog
        open={!!bulkAsk}
        title="Mehrere Dateien herunterladen"
        message={
          <>
            <b>{bulkAsk?.length}</b> Dateien werden nacheinander heruntergeladen. Der Browser fragt
            dabei eventuell nach, ob er mehrere Downloads erlauben soll.
          </>
        }
        confirmLabel="Herunterladen"
        tone="primary"
        onConfirm={() => {
          const list = bulkAsk ?? [];
          setBulkAsk(null);
          void downloadMany(list);
        }}
        onCancel={() => setBulkAsk(null)}
      />

      {nameDialog && (
        <NamenEingabeDialog
          titel={nameDialog.art === "neu" ? "Neuer Ordner" : "Ordner umbenennen"}
          feldName="Ordnername"
          icon={nameDialog.art === "neu" ? I.plus : I.edit}
          wert={nameDialog.name}
          onWert={(name) => {
            setNameDialog((n) => (n ? { ...n, name } : n));
            if (nameErr) setNameErr(null);
          }}
          ort={`in ${(nameDialog.art === "neu" ? nameDialog.eltern : parentPath(nameDialog.alt.path)).replace(/\/$/, "") || WURZEL_LABEL}`}
          hinweis={
            UNGUELTIGE_ZEICHEN.test(nameDialog.name)
              ? 'Nicht möglich in Ordnernamen: „/“, „\\“ und : * ? " < > |'
              : undefined
          }
          fehler={nameErr}
          busy={nameBusy}
          bestaetigenText={nameDialog.art === "neu" ? "Anlegen" : "Umbenennen"}
          onSpeichern={() => void nameSpeichern()}
          onAbbrechen={() => setNameDialog(null)}
        />
      )}

      <ConfirmDialog
        open={!!ordnerLoeschen}
        title="Ordner löschen"
        message={
          <>
            „<b>{ordnerLoeschen?.name}</b>" löschen? Alle Dateien darin wandern in den Papierkorb und
            lassen sich über „Gelöschte anzeigen" wiederherstellen.
          </>
        }
        confirmLabel="Löschen"
        tone="danger"
        onConfirm={() => void ordnerLoeschenBestaetigt()}
        onCancel={() => setOrdnerLoeschen(null)}
      />

      {verschiebeAuswahl && (
        <OrdnerWaehlenDialog
          contactId={contactId}
          titel={
            verschiebeAuswahl.folders.length
              ? `Ordner „${verschiebeAuswahl.folders[0].name}“ verschieben nach…`
              : verschiebeAuswahl.files.length === 1
                ? `„${verschiebeAuswahl.files[0].name}“ verschieben nach…`
                : `${verschiebeAuswahl.files.length} Dateien verschieben nach…`
          }
          startPfad={beschreibbar ? currentPath : ""}
          // Ein Ordner lässt sich nicht in sich selbst oder einen seiner Unterordner schieben.
          ausblenden={(p) => verschiebeAuswahl.folders.some((f) => p.startsWith(normalizeFolderPath(f.path)))}
          onClose={() => setVerschiebeAuswahl(null)}
          onWahl={(ziel) => {
            const auswahl = verschiebeAuswahl;
            setVerschiebeAuswahl(null);
            void verschieben(auswahl, ziel);
          }}
        />
      )}
      {zuweisenOffen && (
        <AkteZuweisenPopup
          contactId={contactId}
          onClose={() => setZuweisenOffen(false)}
          onAssigned={(f) => {
            setToast(`„${f.folderName}" zugewiesen — die Dokumente werden in den nächsten Minuten gebucht.`);
            strukturNeuLaden();
          }}
        />
      )}

      <Toast open={!!toast} message={toast ?? ""} onClose={() => setToast(null)} />
    </>
  );
};
